import { useEffect, useRef, useState } from 'react';
import {
  line, Color, DEFAULT_CURRENCY_CODE, fill, Size, discountColumnShown, quoteAcceptanceSentence, quoteOptionTotals, type QuoteAcceptanceView, formatCurrency, printFamilyOf, printJurisdictionTaxTotals, printPartyBlock, printPartyLines, printTaxRowFacts, t, taxJurisdictionRowLead,
  type DiscountLevel, type DocumentDeliveryView, type PrintDocument, type QuotePrimaryAction, type SnapshotComparison, type SnapshotTaxRow, type StringKey, type TaxJurisdictionTotal, printDocumentTitle,
} from '@bms2/shared';
import { Button, Checkbox, DatePicker, Select, TextInput, Textarea } from '../../components/controls';
import { AlertBanner } from '../../components/controls/AlertBanner';
import { FormFrame } from '../../components/forms/FormFrame';
import { Modal } from '../../components/modals/Modal';
import modalStyles from '../../components/modals/modal.module.css';
import { get, post } from './api';
import { DocumentCopyModal } from './DocumentCopyModal';
import { DeliveredPanel } from './DeliveredPanel';
import { StaleSendBanner, staleSendFor, type StaleSendBasisView } from './StaleSendWarning';
import { Money } from '../../components/Money';
import { DocumentLines, listPricedCell, type DocumentLinesDescriptor } from './DocumentViewPage';
import pageStyles from './RecordPage.module.css';
import styles from './QuotePanels.module.css';
import { ApprovalDecision, type OpenFloor } from '../approvals/ApprovalDecision';
import { quoteFloorAcknowledgementPath } from './QuoteFloor';
import { useDayText } from '../../shell/viewerDates';

/** The Quote's own routes, relative to the API root like every path here. */
export const QUOTES_PATH = '/quotes';

/** A stored line as the Quote's page hands it to the Item Table. */
export interface QuoteLineView {
  id: string;
  lineNumber: number;
  itemId: string | null;
  itemLabel: string;
  sku: string | null;
  description: string;
  quantity: number;
  unitId: string;
  unitName: string;
  unitPriceCents: number;
  referenceCents: number | null;
  discountType: string | null;
  discountValue: number | null;
  taxCategoryId: string;
  taxCategoryName: string;
  accountId: string | null;
  accountLabel: string | null;
  priceSheetName: string | null;
  amountCents: number;
  /** The tax the Quote's save worked out for the line (decision 0645); absent from an older reply. */
  taxAmountCents?: number;
  /** 🔒 An allowance the customer authorizes, billed at actuals from the job (decision 2612); absent from an older reply. */
  isAllowance?: boolean;
  /** 🔒 A line the customer may take or leave (decision 2650); absent from an older reply. */
  isOptional?: boolean;
  /** 🔒 The sheet's taxable mark and its list price, frozen at the send (decision 10040); absent from an older reply. */
  taxable?: boolean;
  listPriceCents?: number | null;
}

/** The split totals over the page's own lines (decision 2650): `taken` is the acceptance's optional lines, null before an answer. */
const optionTotalsOf = (lines: readonly QuoteLineView[], taken: readonly string[] | null) =>
  quoteOptionTotals(lines.map((row) => ({ id: row.id, isOptional: row.isOptional === true, amountCents: row.amountCents, taxCents: row.taxAmountCents ?? 0 })), taken);

/** One sent copy, headed by the revision it went out as rather than by its internal number (decision 0012). */
export interface QuoteVersionView {
  id: string;
  versionNumber: number;
  heading: string;
  sentAt: string;
  sentByName: string;
}


/**
 * The Quote as the print layout draws it (piece 27; decision 0409). Region 2 is
 * our own name, Region 5 the account's bill-to, Region 6 its ship-to, and
 * Region 8 the lines through the print column set. The screen and the PDF
 * render one projection.
 */
export function quotePrintDocument(facts: Readonly<Record<string, string | boolean | null>>, lines: readonly QuoteLineView[]): PrintDocument {
  const text = (key: string) => (typeof facts[key] === 'string' ? (facts[key] as string) : '');
  const money = (amount: number) => formatCurrency({ amount, currencyCode: DEFAULT_CURRENCY_CODE });
  // 🔒 T3782: Regions 5 and 6 as the server stacked them through the print's own rule, drawn as sent, so the sheet is the PDF's.
  const billTo = printPartyBlock(facts.billToBlock);
  const shipTo = printPartyBlock(facts.shipToBlock);
  // 🔒 The optional lines are marked on the sheet and totalled apart, beside the most the customer can take (decision 2650).
  const options = optionTotalsOf(lines, null);
  return {
    documentType: 'Quote',
    family: printFamilyOf('Quote'),
    title: printDocumentTitle('Quote'),
    number: text('number'),
    issuer: { heading: t('document.print.issuedBy'), name: text('organizationName'), lines: printPartyLines(facts.issuerAddress) },
    recipient: { heading: t('document.print.billTo'), name: billTo?.name ?? text('companyName'), lines: billTo?.lines ?? [] },
    deliverTo: shipTo ? { heading: t('document.print.shipTo'), ...shipTo } : null,
    information: [
      { label: t('quote.new.quoteDate'), value: text('quoteDate') },
      { label: t('quote.new.expiryDate'), value: text('expiryDate') },
      { label: t('quote.new.paymentTerms'), value: text('paymentTerms') },
    ].filter((fact) => fact.value !== ''),
    notice: text('escalationReason') || null,
    lines: lines.map((row) => ({
      lineNumber: String(row.lineNumber),
      // 🔒 The Item cell stacks the name, the SKU and the description (decision 0640); a free-text line is its text alone.
      item: row.itemId === null ? '' : row.itemLabel,
      sku: row.sku ?? '',
      description: line(row.isOptional ? `${t('document.print.optionalMark')} ·` : '', row.description || (row.itemId === null ? row.itemLabel : '')),
      // A Quote line names no manufacturer part on screen; the printed column stands and the cell is empty.
      mfrPart: '',
      qty: line(String(row.quantity), row.unitName),
      unitPrice: money(row.unitPriceCents),
      discount: row.discountValue === null || row.discountValue === undefined ? '' : `${row.discountValue}${row.discountType === 'Percentage' ? '%' : ''}`,
      amount: money(row.amountCents),
    })),
    totals: [
      { label: t('document.print.total'), value: text('totalText') },
      ...(options.hasOptional
        ? [{ label: t('document.print.optionalLines'), value: money(options.optionalCents + options.optionalTaxCents) }, { label: t('document.print.allOptions'), value: money(options.allCents + options.allTaxCents) }]
        : []),
    ],
    terms: [],
  };
}

/**
 * 🔒 Who accepted the Quote, on its page (decision 2650): *Accepted from the
 * emailed link by {typed name} on {date} from {address}*, or the person who
 * recorded it, with the optional lines taken where the Quote has any.
 */
export function QuoteAcceptanceLine({ acceptance, optionalCount }: { acceptance: QuoteAcceptanceView | null | undefined; optionalCount: number }) {
  const dayText = useDayText();
  if (!acceptance) return null;
  const taken = optionalCount > 0 ? fill(t('quote.acceptance.optional'), { taken: String(acceptance.optionalLineIds.length), total: String(optionalCount) }) : null;
  return (
    <p className={styles.acceptance} data-quote-acceptance={acceptance.via}>
      {quoteAcceptanceSentence(acceptance, dayText(acceptance.at))}
      {taken ? <span className={styles.acceptanceTaken} data-quote-acceptance-optional={acceptance.optionalLineIds.length}>{taken}</span> : null}
    </p>
  );
}

/** A line's discount as it reads on the tile: a percentage carries its sign, a flat figure is the amount itself. */
const discountText = (row: QuoteLineView): string => `${row.discountValue ?? ''}${row.discountType === 'Percentage' ? '%' : ''}`;

/**
 * 🔒 The Quote's Lines tile (piece 22; decision 0438): the lines as they stand,
 * with the money beneath, drawn the way the Invoice's and the Sales Order's
 * pages draw theirs. It is read-only.
 *
 * 💰 It used to be the editable Item Table with its own Save and Discard, which
 * made the Quote the one accounting document with two ways to change a line —
 * this tile and the Edit form — over two different column sets. The tile's own
 * set never carried the account, so a line saved here took the Item's default
 * and a line saved through Edit took whatever the person picked. Decision 0409
 * already says view mode draws the document and Edit is what reopens it.
 */
export function QuoteLines({ lines, discountLevel, editable, currencyCode = DEFAULT_CURRENCY_CODE, taxRows = [], lineFloors = [], takenOptionalIds = null }: {
  lines: readonly QuoteLineView[];
  /** 🔒 The optional lines the acceptance took (decision 2650); null before an answer, when no optional line counts in the Total. */
  takenOptionalIds?: readonly string[] | null;
  /** 🔒 The lines under the floor of the BOM rows they name, each drawn on its own line (T920; decision 1695). */
  lineFloors?: readonly { lineId: string; message: string; acknowledgement: { reason: string } | null }[];
  /** 🔒 The rows the Quote stored, one per jurisdiction and one per reduced rate (decisions 0738, 1001); an older Quote has none and draws its one Tax row. */
  taxRows?: readonly TaxJurisdictionTotal[];
  discountLevel: DiscountLevel;
  /** Whether the Quote is still a Draft; a frozen revision says why nothing here can change. */
  editable: boolean;
  currencyCode?: string;
}) {
  const showDiscount = discountColumnShown(discountLevel);
  // 🔒 The Total counts the required lines and the optional lines taken; the optional lines stand apart beneath it (decision 2650).
  const options = optionTotalsOf(lines, takenOptionalIds);
  const subtotalCents = options.takenCents;
  const taxCents = options.takenTaxCents;
  const totalLabel = options.hasOptional && takenOptionalIds !== null ? t('quote.lines.acceptedTotal') : t('quote.lines.total');
  const moneyOf = (amount: number) => <Money cents={amount} currencyCode={currencyCode} />;
  const optionRows = options.hasOptional
    ? [
        { label: t('quote.lines.optionalTotal'), value: moneyOf(options.optionalCents + options.optionalTaxCents), attributes: { 'data-quote-optional-total': String(options.optionalCents + options.optionalTaxCents) } },
        { label: t('quote.lines.allOptions'), value: moneyOf(options.allCents + options.allTaxCents), attributes: { 'data-quote-all-options': String(options.allCents + options.allTaxCents) } },
      ]
    : [];
  /**
   * 🔒 The Quote's rows as the one read-only table draws every kind's (decision
   * 0612). Its own two columns — the tax category and the account — and the
   * discount where the level shows one are cells of that table, carrying the
   * attributes they always carried (decision 0435).
   */
  const descriptor: DocumentLinesDescriptor = {
    title: t('quote.lines.title'),
    rowAttribute: 'data-quote-line',
    empty: t('quote.lines.empty'),
    rows: lines.map((row) => {
      const floor = lineFloors.find((breach) => breach.lineId === row.id);
      const floorText = floor ? (floor.acknowledgement ? line(floor.message, '·', t('quote.floor.lineAcknowledged')) : floor.message) : null;
      // 🔒 An allowance line says so on its note, ahead of any floor warning (decision 2612); an optional line says so the same way (decision 2650).
      const mark = row.isAllowance ? t('item.allowance') : null;
      const allowance = row.isOptional ? (mark ? line(mark, '·', t('item.optional')) : t('item.optional')) : mark;
      const marked = { ...(row.isAllowance ? { 'data-quote-line-allowance': 'true' } : {}), ...(row.isOptional ? { 'data-quote-line-optional': 'true' } : {}) };
      return {
      id: row.id,
      label: row.sku ? line(row.sku, '·', row.description || row.itemLabel) : row.description || row.itemLabel,
      // 🔒 The warning sits on the line it is about, and says once it is acknowledged (T920; decision 1695).
      ...(floor ? { note: allowance && floorText ? line(allowance, '·', floorText) : floorText, noteAttributes: { 'data-quote-line-floor': floor.acknowledgement ? 'acknowledged' : 'open', ...marked } } : allowance ? { note: allowance, noteAttributes: marked } : {}),
      cells: [
        line(String(row.quantity), row.unitName),
        listPricedCell(moneyOf(row.unitPriceCents), row.listPriceCents, currencyCode),
        { text: row.taxCategoryName, attributes: { 'data-quote-line-tax': row.taxCategoryId } },
        { text: row.accountLabel ?? '', attributes: { 'data-quote-line-account': row.accountId ?? '' } },
        ...(showDiscount && row.discountValue !== null && row.discountValue !== undefined
          ? [{ text: discountText(row), attributes: { 'data-quote-line-discount': String(row.discountValue) } }]
          : []),
      ],
      amount: moneyOf(row.amountCents),
      taxable: row.taxable === true,
      };
    }),
    // 🔒 The Quote carries the tax its save worked out (decision 0645), so a Quote whose lines carry any draws the Sub Total and the Tax above
    // the Total, and the Total is what the sheet above it prints. A Quote whose lines carry none draws the Total alone, as it always did.
    totals: lines.length === 0 ? [] : taxCents === 0 && options.allTaxCents === 0
      ? [{ label: totalLabel, value: moneyOf(subtotalCents), attributes: { 'data-quote-total': String(subtotalCents) } }, ...optionRows]
      : [
          { label: t('document.print.subtotal'), value: moneyOf(subtotalCents), attributes: { 'data-quote-subtotal': String(subtotalCents) } },
          // 🔒 One Tax line per jurisdiction the Quote stored, named with its rate and base, never the kind of tax (decisions 0738, 1001).
          // 🔒 A Quote with optional lines draws one Tax row over the lines its Total counts, since its stored rows cover every line (decision 2650).
          ...(taxRows.length > 0 && !options.hasOptional
            ? taxRows.map((row) => ({ key: `tax-${row.level}-${row.label}`, label: <>{taxJurisdictionRowLead(row)} {moneyOf(row.taxableBaseCents)}</>, value: moneyOf(row.taxCents), attributes: { 'data-quote-tax': String(row.taxCents), 'data-tax-level': row.level } }))
            : [{ label: t('tax.preview.tax'), value: moneyOf(taxCents), attributes: { 'data-quote-tax': String(taxCents) } }]),
          { label: totalLabel, value: moneyOf(subtotalCents + taxCents), attributes: { 'data-quote-total': String(subtotalCents + taxCents) } },
          ...optionRows,
        ],
  };
  return (
    <div className={styles.lines} data-quote-lines={editable ? 'editable' : 'frozen'} data-quote-line-count={lines.length}>
      <DocumentLines lines={descriptor} />
      {editable ? null : <p className={pageStyles.hint} data-quote-lines-frozen="true">{t('quote.lines.frozen')}</p>}
    </div>
  );
}

/**
 * 🔒 A frozen copy's tax rows through Region 9's own formatter, each with the
 * base it was charged on, as in *Pennsylvania sales tax (6.00%) on $800.00
 * $48.00* (decision 0738). The sheet prints no base since decision 1061; the
 * screen keeps it, as the totals box does. A copy written before format 5
 * carries none.
 */
function taxRowsText(value: unknown): string {
  const rows = Array.isArray(value) ? value.filter((row): row is SnapshotTaxRow => row !== null && typeof row === 'object' && typeof (row as SnapshotTaxRow).label === 'string') : [];
  if (rows.length === 0) return '—';
  return printTaxRowFacts(printJurisdictionTaxTotals(rows), DEFAULT_CURRENCY_CODE, { withBase: true }).map((fact) => `${fact.label} ${fact.value}`).join(' · ');
}

/**
 * The version history tile (decisions 0159, 0490): every Send's frozen copy,
 * newest first and headed by the revision it went out as, with any two
 * compared side by side through `GET <path>/:id/versions/compare`.
 *
 * 🔨 One tile for every versioned document, not a copy per page. `path` and
 * `prefix` are the only things that differ, and the Quote's own defaults keep
 * its data attributes exactly where its tests read them.
 */
export function QuoteVersions({ quoteId, rows, apiBaseUrl = '', onError, path = QUOTES_PATH, prefix = 'quote', documentType = 'Quote' }: {
  quoteId: string;
  rows: readonly QuoteVersionView[];
  apiBaseUrl?: string;
  onError: (message: string) => void;
  /** The document's own route root, so the same tile serves a Purchase Order. */
  path?: string;
  /** What the data attributes are named after, so a page's own tests read their own marks. */
  prefix?: string;
  /** 🔒 The document's kind, which an entry's Open reads its copy under (T277; decision 0773). */
  documentType?: string;
}) {
  const dayText = useDayText();
  /** 🔒 The copy an entry's Open named, drawn read-only as it went out (decision 0773). */
  const [opened, setOpened] = useState<number | null>(null);
  const newest = rows[0]?.versionNumber ?? 1;
  const [from, setFrom] = useState(String(rows.length > 1 ? rows[1]!.versionNumber : newest));
  const [to, setTo] = useState(String(newest));
  const [comparison, setComparison] = useState<SnapshotComparison | null>(null);

  const compare = async () => {
    const answer = await get<SnapshotComparison>(`${path}/${quoteId}/versions/compare?a=${from}&b=${to}`, apiBaseUrl);
    if (!answer.ok) return onError(answer.message);
    setComparison(answer.data);
  };
  const label = (value: unknown) => (value === null || value === undefined || value === '' ? '—' : String(value));
  const picker = (which: 'from' | 'to', value: string, onChange: (next: string) => void) => (
    <Select size={Size.Large} label={t(which === 'from' ? 'quote.versions.from' : 'quote.versions.to')} value={value} onChange={onChange} options={rows.map((row) => ({ value: String(row.versionNumber), label: row.heading }))} dataAttribute={{ [`data-${prefix}-compare`]: which }} />
  );

  if (rows.length === 0) return <p className={pageStyles.hint} {...{ [`data-${prefix}-versions`]: '0' }}>{t('quote.versions.empty')}</p>;
  return (
    <div className={styles.versions} {...{ [`data-${prefix}-versions`]: String(rows.length) }}>
      <ul className={styles.versionList}>
        {rows.map((row) => (
          <li key={row.id} className={styles.version} {...{ [`data-${prefix}-version`]: String(row.versionNumber) }}>
            <span className={styles.versionHeading}>{row.heading}</span>
            <span className={styles.versionDetail}>{line(dayText(row.sentAt), t('quote.versions.sentBy'), row.sentByName)}</span>
            <span {...{ [`data-${prefix}-version-open`]: String(row.versionNumber) }}>
              <Button size={Size.Small} text={[t('document.copy.open')]} actions={[() => setOpened(row.versionNumber)]} />
            </span>
          </li>
        ))}
      </ul>
      {opened !== null ? <DocumentCopyModal key={opened} documentType={documentType} documentId={quoteId} versionNumber={opened} apiBaseUrl={apiBaseUrl} onClose={() => setOpened(null)} /> : null}
      {rows.length > 1 ? (
        <div className={styles.compareBar}>
          {picker('from', from, setFrom)}
          {picker('to', to, setTo)}
          <span {...{ [`data-${prefix}-action`]: 'compare' }}>
            <Button size={Size.Small} text={[t('quote.versions.compare')]} actions={[() => void compare()]} disabled={from === to} />
          </span>
        </div>
      ) : null}
      {comparison ? (
        <div className={styles.comparison} {...{ [`data-${prefix}-comparison`]: comparison.same ? 'same' : 'different' }}>
          {comparison.same ? <p className={pageStyles.hint}>{t('quote.versions.same')}</p> : null}
          {comparison.header.map((field) => (
            <p key={field.field} className={styles.difference} {...{ [`data-${prefix}-difference`]: field.field }}>
              {/* 🔒 The tax rows each copy went out with, as its sheet printed them (decision 0738). */}
              {field.field === 'taxRows'
                ? line(t('quote.versions.header'), '·', field.field, ':', taxRowsText(field.from), '→', taxRowsText(field.to))
                : /* 🔒 The terms a copy went out with are sections of text, not one value; the comparison says they changed (decision 0703). */
                (field.from !== null && typeof field.from === 'object') || (field.to !== null && typeof field.to === 'object')
                  ? line(t('quote.versions.header'), '·', field.field, ':', t('quote.versions.textChanged'))
                  : line(t('quote.versions.header'), '·', field.field, ':', label(field.from), '→', label(field.to))}
            </p>
          ))}
          {comparison.lines.map((row) => (
            <p key={row.lineNumber} className={styles.difference} {...{ [`data-${prefix}-difference-line`]: String(row.lineNumber) }}>
              {row.kind === 'changed'
                ? line(t('quote.versions.line'), String(row.lineNumber), '·', row.fields.map((field) => `${field.field} ${label(field.from)} → ${label(field.to)}`).join(', '))
                : line(t('quote.versions.line'), String(row.lineNumber), '·', t(row.kind === 'added' ? 'quote.versions.added' : 'quote.versions.removed'))}
            </p>
          ))}
          <span {...{ [`data-${prefix}-action`]: 'closeComparison' }}>
            <Button size={Size.Small} color={Color.Inactive} text={[t('quote.versions.close')]} actions={[() => setComparison(null)]} />
          </span>
        </div>
      ) : null}
    </div>
  );
}

const PRIMARY_LABEL: Readonly<Record<QuotePrimaryAction, StringKey>> = {
  send: 'quote.action.send',
  markAccepted: 'quote.action.markAccepted',
  extendExpiry: 'quote.action.extendExpiry',
  convert: 'quote.action.convert',
  openSalesOrder: 'quote.action.openSalesOrder',
};

type QuoteDialog = 'send' | 'markSent' | 'accept' | 'convert' | 'extendExpiry' | 'void' | 'decline' | 'floorWarning' | null;

export interface QuoteActionsProps {
  quoteId: string;
  /** The page answer's own `canApprove`, which draws Approve and Reject (T644; decision 1521). */
  canApprove?: boolean;
  primary: QuotePrimaryAction | null;
  primaryDisabledReason: string | null;
  /**
   * 🔒 What the Draft's one primary action reads, off the gate's dry run (T223;
   * decision 0713): `submitForApproval` where a rule would hold the Quote, and
   * `send` otherwise; the hint names who it goes to and why.
   */
  sendAction?: 'send' | 'submitForApproval' | null;
  sendHint?: string | null;
  offersDecline: boolean;
  offersVoid: boolean;
  expiryDate: string | null;
  /** The move the URL asked for after a save: `send` where Save and Send landed here, null otherwise (decision 0409). */
  askedAction?: 'send' | null;
  /** Whether Edit is offered at all: every status but a frozen one (decisions 0409, 0474). */
  offersEdit?: boolean;
  /**
   * 🔨 The revision an edit would produce, on a status where editing reopens
   * the Quote rather than changing it in place — null on a Draft, which edits
   * as itself (decisions 0172, 0474). Set, the control says so on its face,
   * because 0172 refuses a prompt and a label is not a prompt.
   */
  reviseAs?: string | null;
  /** Why a frozen revision offers nothing, in the server's own words (decisions 0458, 0474). */
  frozenReason?: string | null;
  /** Reopening the form is a route change, so the page does it and this component never routes. */
  onEdit?: () => void;
  apiBaseUrl?: string;
  onDone: (message: string) => void;
  onError: (message: string) => void;
  /** A Duplicate opens the new Draft; the page navigates, so this component never routes. */
  onOpenQuote: (quoteId: string) => void;
  /**
   * 🔒 The live Sales Order this Quote became, which the primary opens in
   * Convert's place and names (T438; decision 1121); null where none stands.
   */
  convertedSalesOrder?: { id: string; number: string } | null;
  /** Opening the order is a route change, so the page does it. */
  onOpenSalesOrder?: (salesOrderId: string) => void;
  /** 🔒 The earlier send's Delivered panel, which the send window draws beneath its fields (decision 1226); null where none went. */
  previousDelivery?: DocumentDeliveryView | null;
  /** The viewer's zone, for the panel's times (decision 0261). */
  timeZone?: string;
  /** 🔒 The date and the threshold the send window warns on, on a Quote awaiting its first send (decision 1227). */
  staleSend?: StaleSendBasisView | null;
  /** Change the Date opens the page's own date correction; the page does it, so this component never routes. */
  onChangeDate?: () => void;
  /**
   * 🔒 Whether the accept window offers Convert to a Sales Order: the page's own
   * answer that the Deal is not Lost, because the acceptance wins a working Deal
   * (T1001, T1189; decisions 1714, 2091). A tick the server would refuse is never drawn.
   */
  offersConvert?: boolean;
  /**
   * 🔒 A count the page raises when the What's next banner's step is pressed
   * (decision 1732): each rise opens the primary's own window, the one the footer
   * button opens, so the banner invents no window of its own.
   */
  primaryRequest?: number;
  /**
   * 🔒 The floors no reason covers yet, off the page's own reads (T1195; decision
   * 1861). Submit for approval warns on them in a window first, and the approver's
   * Approve asks for the override in one.
   */
  openFloors?: readonly OpenFloor[];
  /**
   * 🔒 The Quote's optional lines, which the accept window lists with a tick
   * each, every one ticked to start (decision 2650): only the lines left ticked
   * carry to the Sales Order and count in the Deal's value.
   */
  optionalLines?: readonly { id: string; label: string }[];
  /**
   * 🔒 The account over its credit limit or on Credit Hold, off the page's own
   * facts (decision 3280; T2269): the accept window's Convert and the Convert
   * window name it before the order is made, and neither refuses on it.
   */
  creditWarning?: string | null;
}

/**
 * 🔒 The Quote's footer carries one primary action and the status decides which
 * (product owner, 2026-09-14): Draft sends, Sent and ClientViewed mark
 * accepted, Expired extends the expiry, Accepted converts. Send is refused with
 * its reason on the control while the gate holds the Quote. Duplicate stands in
 * every status; Void is the company withdrawing and Mark as Declined is the
 * customer's answer, and 🔨 the two are never one control.
 */
export function QuoteActions({ quoteId, canApprove = false, primary, primaryDisabledReason, sendAction = null, sendHint = null, offersDecline, offersVoid, expiryDate, askedAction = null, offersEdit = false, reviseAs = null, frozenReason = null, onEdit, apiBaseUrl = '', onDone, onError, onOpenQuote, convertedSalesOrder = null, onOpenSalesOrder, previousDelivery = null, timeZone = 'UTC', staleSend = null, onChangeDate, offersConvert = true, primaryRequest = 0, openFloors = [], optionalLines = [], creditWarning = null }: QuoteActionsProps) {
  // 🔨 Save and Send lands on the saved Quote with the send asked for, so a send that fails leaves a saved Quote (decision 0409). The page reads the URL; this component takes the answer.
  const [asked, setAsked] = useState<QuoteDialog>(null);
  const [askDone, setAskDone] = useState(false);
  const dialog: QuoteDialog = asked ?? (askedAction === 'send' && !askDone && primary === 'send' ? 'send' : null);
  const setDialog = (next: QuoteDialog) => {
    setAsked(next);
    if (next === null) setAskDone(true);
  };
  const [busy, setBusy] = useState(false);
  const [to, setTo] = useState('');
  const [message, setMessage] = useState('');
  const [convert, setConvert] = useState(false);
  const [customerPo, setCustomerPo] = useState('');
  const [expiry, setExpiry] = useState(expiryDate ?? '');
  const [reason, setReason] = useState('');
  // 🔒 The optional lines the rep unticked in the accept window; every optional line is taken until one is (decision 2650).
  const [untaken, setUntaken] = useState<ReadonlySet<string>>(new Set());
  // 🔒 The Convert window's refusal, drawn inside its frame rather than on the page behind it (decisions 3060, 3580).
  const [convertRefusal, setConvertRefusal] = useState<string | null>(null);
  const close = () => {
    setDialog(null);
    setConvertRefusal(null);
  };

  const act = async <T,>(path: string, body: unknown, note: (data: T) => string) => {
    setBusy(true);
    const answer = await post<T>(`${QUOTES_PATH}/${quoteId}/${path}`, body, apiBaseUrl);
    setBusy(false);
    close();
    if (!answer.ok) return onError(answer.message);
    // 🔨 The page keeps this bar mounted through the reload that follows (decision 1431), so a dialog's typed words are cleared here, not by a remount.
    setTo('');
    setMessage('');
    setReason('');
    setConvert(false);
    setCustomerPo('');
    onDone(note(answer.data));
  };

  // 🔒 A first send of an old-dated Quote warns, and its primary is Send Anyway, which the server's audit row records (decision 1227).
  const stale = staleSendFor('Quote', staleSend, timeZone);
  const send = () =>
    act<{ status: string; to: string; sent: string }>('send', { to: to.trim() || null, message: message.trim() || null, ...(stale ? { sendAnyway: true } : {}) }, (data) =>
      data.sent === 'Accepted' || data.sent === 'Deferred' ? line(t('quote.send.sentTo'), data.to) : t('quote.send.failed'),
    );
  // Accepting offers Convert to Sales Order in the same dialog, and converting stays a second act (piece 22).
  const accept = () =>
    // 🔒 The customer's number rides with Convert alone and is never asked for (T929; decision 1694).
    act<{ offer: { kind: string } | null; salesOrderId: string | null }>('accept', {
      convert: convert && offersConvert,
      ...(convert && offersConvert && customerPo.trim() ? { customerPoNumber: customerPo.trim() } : {}),
      // 🔒 The optional lines left ticked, which alone carry to the order (decision 2650).
      ...(optionalLines.length > 0 ? { optionalLineIds: optionalLines.filter((row) => !untaken.has(row.id)).map((row) => row.id) } : {}),
    }, (data) =>
      data.salesOrderId ? t('quote.accept.converted') : data.offer?.kind === 'offered' ? t('quote.accept.offered') : t('quote.accept.accepted'),
    );
  /**
   * 🔒 Convert (decision 3580; T2282): the credit warning is read in the window and the order is made all the same.
   * A refusal stays inside the window, which stays open on it, so the number typed is not lost.
   */
  const doConvert = async () => {
    setBusy(true);
    const answer = await post<{ salesOrderNumber: string }>(`${QUOTES_PATH}/${quoteId}/convert`, customerPo.trim() ? { customerPoNumber: customerPo.trim() } : {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return setConvertRefusal(answer.message);
    close();
    setCustomerPo('');
    onDone(line(t('quote.convert.converted'), answer.data.salesOrderNumber));
  };
  const extend = () => act('extend-expiry', { expiryDate: expiry }, () => line(t('quote.expiry.extended'), expiry));
  const decline = () => act('decline', {}, () => t('quote.decline.declined'));
  const withdraw = () => act('void', { reason: reason.trim() }, () => t('quote.void.voided'));
  const duplicate = async () => {
    setBusy(true);
    const answer = await post<{ id: string; quoteNumber: string }>(`${QUOTES_PATH}/${quoteId}/duplicate`, {}, apiBaseUrl);
    setBusy(false);
    if (!answer.ok) return onError(answer.message);
    onDone(line(t('quote.duplicate.duplicated'), answer.data.quoteNumber));
    onOpenQuote(answer.data.id);
  };

  // 🔒 Submit for approval routes and holds, and nothing is mailed; the approver's Approve is what sends it (T223; decision 0713).
  const submitsForApproval = primary === 'send' && sendAction === 'submitForApproval';
  const submit = () => act<{ submitted?: boolean; to: string }>('send', {}, (data) => (data.submitted ? t('approval.submitted') : line(t('quote.send.sentTo'), data.to)));
  const openPrimary = () => {
    if (primary === 'openSalesOrder') {
      if (convertedSalesOrder && onOpenSalesOrder) onOpenSalesOrder(convertedSalesOrder.id);
      return;
    }
    // 🔒 Convert opens a window offering the customer's number, the Issue Sales Order step's optional prompt (T929; decision 1694).
    if (primary === 'convert') return setDialog('convert');
    // 🔒 Submit below the floor warns in a window before anything is written (T1195; decision 1861).
    if (submitsForApproval) return openFloors.length > 0 ? setDialog('floorWarning') : void submit();
    setDialog(primary === 'markAccepted' ? 'accept' : primary === 'extendExpiry' ? 'extendExpiry' : 'send');
  };
  // 🔒 The banner's press opens what the footer's primary opens (decision 1732); the count it last answered is kept, so a reload never reopens it.
  const answered = useRef(primaryRequest);
  useEffect(() => {
    if (primaryRequest === answered.current) return;
    answered.current = primaryRequest;
    openPrimary();
  });
  // 🔒 The same moves as Send, with nothing emailed (decision 0436).
  const markSent = () => act('mark-sent', {}, () => t('document.markedSent'));

  return (
    /* 🔒 The bar itself is built once, in `DocumentViewPage` (T168); these are the Quote's own controls inside it. */
    <>
      {/* 🔒 The approval decision on the document it is about (decision 0599, on decision 0504's reasoning). */}
      <ApprovalDecision documentType="Quote" documentId={quoteId} canApprove={canApprove} apiBaseUrl={apiBaseUrl} onDone={onDone} onError={onError} openFloors={openFloors} floorAcknowledgementPath={quoteFloorAcknowledgementPath(quoteId)} />
      {/*
        * 🔒 Edit is offered on every status a reopen allows, not on a Draft alone
        * (decision 0474). A Draft edits as itself; a sent, viewed, declined or
        * expired revision reopens as the next one when it is saved, and the
        * control says which revision that will be rather than asking — decision
        * 0172 refuses the prompt, and a label is not a prompt.
        */}
      {offersEdit && onEdit ? (
        <span data-quote-action="edit" data-quote-revises={reviseAs ?? ''}>
          <Button size={Size.Medium} text={reviseAs ? [line(t('document.revise.action'), reviseAs)] : [t('document.edit.action')]} actions={[onEdit]} disabled={busy} />
        </span>
      ) : null}
      {/* 🔨 A frozen revision offers nothing, and says why and what to do instead, in the server's own sentence (decision 0458). */}
      {!offersEdit && frozenReason ? <span className={styles.footerNote} data-quote-frozen>{frozenReason}</span> : null}
      {primary ? (
        <span data-quote-action={primary}>
          {/* 🔒 Send carries a second entry, Mark as sent, on the same control (decision 0436); the Button splits the moment it is given two. */}
          <Button
            size={Size.Medium}
            color={Color.Navy}
            text={
              submitsForApproval ? [t('approval.submitForApproval')]
                : primary === 'send' ? [t(PRIMARY_LABEL[primary]), t('document.action.markSent')]
                  /* 🔒 Open Sales Order names the order it opens (T438; decision 1121). */
                  : primary === 'openSalesOrder' && convertedSalesOrder ? [line(t(PRIMARY_LABEL[primary]), convertedSalesOrder.number)]
                    : [t(PRIMARY_LABEL[primary])]
            }
            actions={submitsForApproval ? [openPrimary] : primary === 'send' ? [openPrimary, () => setDialog('markSent')] : [openPrimary]}
            disabled={busy || primaryDisabledReason !== null}
          />
        </span>
      ) : null}
      {primaryDisabledReason ? <span className={styles.refusal} data-quote-refusal="true">{primaryDisabledReason}</span> : null}
      {submitsForApproval && sendHint ? <span className={styles.footerNote} data-send-hint="true">{sendHint}</span> : null}
      {/* 🔨 The sheet's own control moved to the action bar above the two views, so one document offers it once (decision 0612). */}
      {offersDecline ? (
        <span data-quote-action="decline">
          <Button size={Size.Small} text={[t('quote.action.decline')]} actions={[() => setDialog('decline')]} disabled={busy} />
        </span>
      ) : null}
      {offersVoid ? (
        <span data-quote-action="void">
          <Button size={Size.Small} color={Color.Inactive} text={[t('quote.action.void')]} actions={[() => setDialog('void')]} disabled={busy} />
        </span>
      ) : null}
      <span data-quote-action="duplicate">
        <Button size={Size.Small} color={Color.Inactive} text={[t('quote.action.duplicate')]} actions={[() => void duplicate()]} disabled={busy} />
      </span>

      {dialog === 'send' ? (
      <Modal
        open
        title={t('quote.send.title')}
        onCancel={close}
        dataModal="quoteSend"
        actions={[{ kind: 'primary', label: stale ? t('document.staleSend.sendAnyway') : t('quote.action.send'), onAct: () => void send(), disabled: busy }, { kind: 'cancel', label: t('modal.cancel'), onAct: close }]}
      >
        <StaleSendBanner
          warning={stale}
          onChangeDate={() => {
            close();
            onChangeDate?.();
          }}
        />
        <TextInput size={Size.Medium} label={t('quote.send.to')} value={to} onChange={setTo} type="email" name="quoteSendTo" />
        <p className={modalStyles.fieldLabel}>{t('quote.send.toHint')}</p>
        <Textarea size={Size.Large} label={t('quote.send.message')} value={message} onChange={setMessage} />
        <DeliveredPanel delivery={previousDelivery} timeZone={timeZone} heading={t('document.delivery.previousTitle')} />
      </Modal>

      ) : null}

      {/* 🔒 Submit below the floor: the breach sentences, Send for approval anyway, and Fix the quote, which writes nothing (T1195; decision 1861). */}
      {dialog === 'floorWarning' ? (
      <Modal
        open
        title={t('quote.floorWarning.title')}
        onCancel={close}
        dataModal="quoteFloorWarning"
        actions={[{ kind: 'primary', label: t('quote.floorWarning.sendAnyway'), onAct: () => void submit(), disabled: busy }, { kind: 'cancel', label: t('quote.floorWarning.fix'), onAct: close }]}
      >
        {openFloors.map((floor) => (
          <p key={floor.lineId ?? 'document'} className={modalStyles.fieldLabel} data-quote-floor-warning={floor.lineId ?? 'document'}>{floor.message}</p>
        ))}
        <p className={modalStyles.fieldLabel}>{t('quote.floorWarning.body')}</p>
      </Modal>
      ) : null}

      {/* 🔒 Mark as sent is confirmed, because the Quote moves to Sent and freezes a revision with nothing emailed (decision 0436). */}
      {dialog === 'markSent' ? (
      <Modal
        open
        title={t('document.markSent.title')}
        onCancel={close}
        dataModal="quoteMarkSent"
        actions={[{ kind: 'primary', label: t('document.action.markSent'), onAct: () => void markSent(), disabled: busy }, { kind: 'cancel', label: t('modal.cancel'), onAct: close }]}
      >
        <p className={modalStyles.fieldLabel}>{t('document.markSent.body')}</p>
      </Modal>
      ) : null}

      {dialog === 'accept' ? (
      <Modal
        open
        title={t('quote.accept.title')}
        onCancel={close}
        dataModal="quoteAccept"
        actions={[{ kind: 'primary', label: t('quote.action.markAccepted'), onAct: () => void accept(), disabled: busy }, { kind: 'cancel', label: t('modal.cancel'), onAct: close }]}
      >
        <p className={modalStyles.fieldLabel}>{t('quote.accept.body')}</p>
        {optionalLines.length > 0 ? (
          <fieldset className={styles.optionalTaken} data-quote-accept-optional="true">
            <legend className={modalStyles.fieldLabel}>{t('quote.accept.optionalHeading')}</legend>
            {optionalLines.map((row) => (
              <Checkbox key={row.id} label={row.label} checked={!untaken.has(row.id)} size={Size.Small} name={`optional-taken-${row.id}`}
                onChange={(checked) => setUntaken((held) => {
                  const next = new Set(held);
                  if (checked) next.delete(row.id);
                  else next.add(row.id);
                  return next;
                })}
              />
            ))}
          </fieldset>
        ) : null}
        {offersConvert ? <Checkbox label={t('quote.accept.convert')} checked={convert} size={Size.Small} onChange={setConvert} /> : null}
        {offersConvert && creditWarning ? <div data-quote-credit-warning="accept"><AlertBanner tone="warning" urging message={creditWarning} /></div> : null}
        <p className={modalStyles.fieldLabel} data-quote-convert-hint="true">{t('quote.accept.convertHint')}</p>
        {convert && offersConvert ? <TextInput size={Size.Medium} label={t('quote.customerPo.number')} value={customerPo} onChange={setCustomerPo} name="quoteCustomerPoNumber" /> : null}
      </Modal>

      ) : null}

      {dialog === 'convert' ? (
      <FormFrame
        host="window"
        open
        title={t('quote.convert.title')}
        onCancel={close}
        dataForm="quoteConvert"
        typed={customerPo.trim() !== ''}
        refusal={convertRefusal}
        onDismissRefusal={() => setConvertRefusal(null)}
        act={{ label: t('quote.action.convert'), onAct: () => doConvert() }}
      >
        <p className={modalStyles.fieldLabel}>{t('quote.convert.body')}</p>
        {/* 🔒 The account on Credit Hold or over its limit is named before the order is made, and the order is made all the same (decisions 3280, 3580). */}
        {creditWarning ? <div data-quote-credit-warning="convert"><AlertBanner tone="warning" urging message={creditWarning} /></div> : null}
        <TextInput size={Size.Medium} label={t('quote.customerPo.number')} value={customerPo} onChange={setCustomerPo} name="quoteCustomerPoNumber" />
        <p className={modalStyles.fieldLabel} data-quote-customer-po-hint="true">{t('quote.customerPo.hint')}</p>
      </FormFrame>

      ) : null}

      {dialog === 'extendExpiry' ? (
      <Modal
        open
        title={t('quote.expiry.title')}
        onCancel={close}
        dataModal="quoteExpiry"
        actions={[{ kind: 'primary', label: t('quote.action.extendExpiry'), onAct: () => void extend(), disabled: busy || !expiry }, { kind: 'cancel', label: t('modal.cancel'), onAct: close }]}
      >
        <p className={modalStyles.fieldLabel}>{t('quote.expiry.body')}</p>
        <DatePicker size={Size.Small} label={t('quote.expiry.date')} value={expiry} onChange={setExpiry} mode="date" name="quoteExpiry" />
      </Modal>

      ) : null}

      {dialog === 'void' ? (
      <Modal
        open
        title={t('quote.void.title')}
        onCancel={close}
        dataModal="quoteVoid"
        actions={[{ kind: 'primary', label: t('quote.void.act'), onAct: () => void withdraw(), disabled: busy || reason.trim().length === 0 }, { kind: 'cancel', label: t('modal.cancel'), onAct: close }]}
      >
        <p className={modalStyles.fieldLabel}>{t('quote.void.body')}</p>
        <TextInput size={Size.Large} label={t('quote.void.reason')} value={reason} onChange={setReason} required name="quoteVoidReason" />
      </Modal>

      ) : null}

      {dialog === 'decline' ? (
      <Modal
        open
        title={t('quote.decline.title')}
        onCancel={close}
        dataModal="quoteDecline"
        actions={[{ kind: 'primary', label: t('quote.action.decline'), onAct: () => void decline(), disabled: busy }, { kind: 'cancel', label: t('modal.cancel'), onAct: close }]}
      >
        <p className={modalStyles.fieldLabel}>{t('quote.decline.body')}</p>
      </Modal>
      ) : null}
    </>
  );
}
